import { createNumberFormatter, EMPTY_VALUE_PLACEHOLDER } from "./core";

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;
const MINUTES_PER_HOUR = 60;

const MILLISECONDS_PER_MINUTE = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
const MILLISECONDS_PER_HOUR = MINUTES_PER_HOUR * MILLISECONDS_PER_MINUTE;

const decimalSecondsFormatter = createNumberFormatter(2, {
  minimumFractionDigits: 0,
});

/**
 * Converte milissegundos em formato legível.
 * * Regras:
 * - < 1000ms: exibe em milissegundos (ex: "500ms")
 * - < 1 min: exibe em segundos (ex: "30s")
 * - < 1 hora: exibe em minutos (ex: "45min")
 * - > 1 hora: exibe horas e minutos (ex: "2h 15min")
 */
export function formatDurationFromMilliseconds(
  totalMs: number | null | undefined
): string {
  if (totalMs === null || totalMs === undefined || totalMs < 0) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  if (totalMs < MILLISECONDS_PER_SECOND) {
    // return `${Math.round(totalMs)}ms`;
    const seconds = totalMs / MILLISECONDS_PER_SECOND;
    return `${decimalSecondsFormatter.format(seconds)}s`;
  }

  if (totalMs < MILLISECONDS_PER_MINUTE) {
    const seconds = Math.round(totalMs / MILLISECONDS_PER_SECOND);
    return `${seconds}s`;
  }

  if (totalMs < MILLISECONDS_PER_HOUR) {
    const minutes = Math.round(totalMs / MILLISECONDS_PER_MINUTE);
    return `${minutes}min`;
  }

  const totalMinutes = Math.floor(totalMs / MILLISECONDS_PER_MINUTE);
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, "0")}min`;
  }

  return `${minutes}min`;
}

export function formatDurationFromSeconds(
  totalSeconds: number | null | undefined
): string {
  if (totalSeconds === null || totalSeconds === undefined) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  return formatDurationFromMilliseconds(totalSeconds * MILLISECONDS_PER_SECOND);
}

export function formatDurationFromMinutes(
  totalMinutes: number | null | undefined
): string {
  if (totalMinutes === null || totalMinutes === undefined) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  return formatDurationFromMilliseconds(totalMinutes * MILLISECONDS_PER_MINUTE);
}

export function formatDurationFromTimeSpan(
  timeSpan: string | null | undefined
): string {
  /**
   * Regex para parsing de TimeSpan do .NET.
   * Padrão: [sinal][dias.]horas:minutos:segundos[.frações]
   * * Grupos de Captura:
   * 1: Sinal negativo (opcional)
   * 2: Dias (opcional)
   * 3: Horas
   * 4: Minutos
   * 5: Segundos (incluindo fração decimal)
   * * Exs válidos: "00:01:06.8350000", "1.05:30:00", "-00:30:00"
   */
  const DOTNET_TIMESPAN_PATTERN =
    /^(-?)(?:(\d+)\.)?(\d+):(\d{2}):(\d{2}(?:\.\d+)?)$/;

  if (!timeSpan) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  const match = DOTNET_TIMESPAN_PATTERN.exec(timeSpan);
  if (!match) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  const [_, signStr, daysStr, hoursStr, minutesStr, secondsStr] = match;

  const isNegative = signStr === "-";
  const days = daysStr ? parseInt(daysStr, 10) : 0;
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  const seconds = parseFloat(secondsStr);

  const totalSeconds =
    days * SECONDS_PER_DAY +
    hours * SECONDS_PER_HOUR +
    minutes * SECONDS_PER_MINUTE +
    seconds;

  const finalMilliseconds = totalSeconds * MILLISECONDS_PER_SECOND;

  const formattedDuration = formatDurationFromMilliseconds(finalMilliseconds);
  return isNegative ? `-${formattedDuration}` : formattedDuration;
}
